import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Fire, Snowflake, MapPinArea, Clock, UsersThree, Lightbulb,
  PencilSimple, Trash, FloppyDisk, X, DoorOpen, Users, Crosshair,
} from "@phosphor-icons/react";

const PERIODS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 Days" },
  { id: "30d", label: "30 Days" },
];
const HEAT = {
  hot: { color: "#FF3B30", label: "HOT" },
  warm: { color: "#FF9F0A", label: "WARM" },
  cold: { color: "#0A84FF", label: "COLD" },
};

// intensity 0-1 -> green (cold) -> yellow -> red (hot), matching a retail density map
function heatColor(v) {
  if (v <= 0) return "transparent";
  const hue = (1 - v) * 120; // 120=green -> 0=red
  const alpha = 0.22 + v * 0.5;
  return `hsla(${hue}, 85%, 50%, ${alpha})`;
}

function rectFromPoly(poly) {
  const xs = poly.map((p) => p[0]), ys = poly.map((p) => p[1]);
  return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys) };
}
function polyFromRect(r) {
  return [[r.x1, r.y1], [r.x2, r.y1], [r.x2, r.y2], [r.x1, r.y2]];
}
const norm = (r) => ({ x1: Math.min(r.x1, r.x2), y1: Math.min(r.y1, r.y2), x2: Math.max(r.x1, r.x2), y2: Math.max(r.y1, r.y2) });

// LiveReach-style store heatmap: real camera frame + density overlay + drawable
// counting zones that each show live Traffic + Dwell.
function StoreHeatmap({ cameras, period }) {
  const [camId, setCamId] = useState("");
  const [map, setMap] = useState(null);
  const [metrics, setMetrics] = useState([]);   // per-zone metrics for THIS camera
  const [zones, setZones] = useState([]);        // [{name, rect}]
  const [lineCfg, setLineCfg] = useState({ line_enabled: false, orientation: "horizontal", position: 0.5 });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const drawing = useRef(false);
  const boxRef = useRef(null);

  useEffect(() => { if (!camId && cameras.length) setCamId(cameras[0].id); }, [cameras, camId]);

  const loadMap = useCallback(() => {
    if (!camId) return;
    api.get(`/analytics/heatmap/${camId}?period=${period}`).then((r) => setMap(r.data)).catch(() => setMap(null));
  }, [camId, period]);

  const loadMetrics = useCallback(() => {
    if (!camId) return;
    api.get(`/analytics/hot-zones?period=${period}`).then((r) => {
      const all = [...(r.data.zones || []), ...(r.data.transit_zones || [])];
      setMetrics(all.filter((z) => z.camera_id === camId));
    }).catch(() => setMetrics([]));
  }, [camId, period]);

  const loadZones = useCallback(() => {
    if (!camId) return;
    api.get(`/cameras/${camId}/counting`).then((r) => {
      const z = Object.entries(r.data.zones || {}).map(([name, poly]) => ({ name, rect: rectFromPoly(poly) }));
      setZones(z);
      setLineCfg({ line_enabled: !!r.data.line_enabled, orientation: r.data.orientation || "horizontal", position: r.data.position ?? 0.5 });
    }).catch(() => setZones([]));
  }, [camId]);

  useEffect(() => { loadZones(); }, [loadZones]);
  useEffect(() => {
    loadMap(); loadMetrics();
    if (editing) return;                           // pause polling while drawing
    const t = setInterval(() => { loadMap(); loadMetrics(); }, 8000);
    return () => clearInterval(t);
  }, [loadMap, loadMetrics, editing]);

  const cells = map?.cells || [];
  const src = map?.frame_b64 ? `data:image/jpeg;base64,${map.frame_b64}` : null;
  const hasHeat = (map?.max || 0) > 0;
  const metricFor = (name) => metrics.find((m) => m.zone === name);

  const toNorm = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };
  const onDown = (e) => {
    if (!editing) return;
    e.preventDefault();
    const p = toNorm(e);
    drawing.current = true;
    setDraft({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };
  const onMove = (e) => {
    if (!editing || !drawing.current) return;
    const p = toNorm(e);
    setDraft((d) => ({ ...d, x2: p.x, y2: p.y }));
  };
  const onUp = () => {
    if (!editing || !drawing.current) return;
    drawing.current = false;
    setDraft((d) => {
      if (!d) return null;
      const r = norm(d);
      if ((r.x2 - r.x1) < 0.04 || (r.y2 - r.y1) < 0.04) return null; // too small
      const name = window.prompt("Name this section (e.g. Apples, Deli Counter, Meat Case):", "");
      if (name && name.trim()) setZones((zs) => [...zs, { name: name.trim().slice(0, 30), rect: r }]);
      return null;
    });
  };

  const removeZone = (i) => setZones((zs) => zs.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    const payload = {
      line_enabled: lineCfg.line_enabled, orientation: lineCfg.orientation, position: lineCfg.position,
      zones: Object.fromEntries(zones.map((z) => [z.name, polyFromRect(z.rect)])),
    };
    try {
      await api.post(`/cameras/${camId}/counting`, payload);
      toast.success("Zones saved — the camera agent picks them up within ~15s.");
      setEditing(false);
      loadMetrics();
    } catch {
      toast.error("Could not save zones.");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-[#121214] border border-white/10 p-4" data-testid="store-heatmap">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm flex items-center gap-2">
          <Fire size={16} weight="fill" className="text-[#FF3B30]" /> Store Heatmap
          <span className="text-[10px] font-mono-data text-[#71717A] normal-case tracking-normal">draw sections to measure traffic &amp; dwell</span>
        </h2>
        <div className="flex items-center gap-2">
          <select
            data-testid="heatmap-camera-select" value={camId} onChange={(e) => setCamId(e.target.value)}
            className="bg-[#1A1A1E] border border-white/10 text-xs font-mono-data px-2 py-1.5 text-white outline-none max-w-[200px]"
          >
            {cameras.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {!editing ? (
            <button data-testid="heatmap-edit-zones" onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 bg-[#BF5AF2]/15 border border-[#BF5AF2]/40 text-[#BF5AF2] text-xs font-mono-data uppercase px-3 py-1.5 hover:bg-[#BF5AF2]/25 transition-colors">
              <PencilSimple size={13} weight="bold" /> Draw Zones
            </button>
          ) : (
            <>
              <button data-testid="heatmap-save-zones" onClick={save} disabled={saving}
                className="flex items-center gap-1.5 bg-[#32D74B]/15 border border-[#32D74B]/40 text-[#32D74B] text-xs font-mono-data uppercase px-3 py-1.5 hover:bg-[#32D74B]/25 transition-colors disabled:opacity-50">
                <FloppyDisk size={13} weight="bold" /> {saving ? "Saving…" : "Save"}
              </button>
              <button data-testid="heatmap-cancel-zones" onClick={() => { setEditing(false); setDraft(null); loadZones(); }}
                className="flex items-center gap-1.5 border border-white/15 text-[#A1A1AA] text-xs font-mono-data uppercase px-3 py-1.5 hover:text-white transition-colors">
                <X size={13} weight="bold" /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {editing && (
        <div className="mb-2 text-[11px] font-mono-data text-[#BF5AF2] flex items-center gap-1.5" data-testid="heatmap-draw-hint">
          <Crosshair size={13} weight="bold" /> Click &amp; drag on the camera image to draw a section box, then name it. Save when done.
        </div>
      )}

      <div
        ref={boxRef}
        className="relative w-full overflow-hidden border border-white/10 bg-black select-none"
        style={{ aspectRatio: "16 / 9", cursor: editing ? "crosshair" : "default", touchAction: "none" }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
      >
        {src ? (
          <img src={src} alt="camera" draggable={false} className="absolute inset-0 w-full h-full object-cover opacity-90" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#71717A] font-mono-data text-xs">No frame yet from this camera</div>
        )}

        {/* density heat overlay */}
        {hasHeat && (
          <div className="absolute inset-0 grid pointer-events-none"
            style={{ gridTemplateColumns: `repeat(${map.gw}, 1fr)`, gridTemplateRows: `repeat(${map.gh}, 1fr)`, filter: "blur(7px)" }}>
            {cells.flatMap((row, r) => row.map((v, c) => <div key={`${r}-${c}`} style={{ backgroundColor: heatColor(v) }} />))}
          </div>
        )}

        {/* saved zones + metrics */}
        {zones.map((z, i) => {
          const m = metricFor(z.name);
          const r = z.rect;
          const w = (r.x2 - r.x1) * 100, h = (r.y2 - r.y1) * 100;
          const cx = (r.x1 + r.x2) / 2 * 100;
          const hot = (m?.heat && HEAT[m.heat]) || HEAT.cold;
          return (
            <div key={i} data-testid={`zone-box-${i}`} className="absolute" style={{ left: `${r.x1 * 100}%`, top: `${r.y1 * 100}%`, width: `${w}%`, height: `${h}%` }}>
              <div className="w-full h-full border-2" style={{ borderColor: hot.color, background: `${hot.color}14` }} />
              {/* name tag */}
              <span className="absolute -top-0.5 left-0 -translate-y-full text-[10px] font-mono-data font-bold px-1.5 py-0.5 whitespace-nowrap"
                style={{ background: hot.color, color: "#000" }}>{z.name}</span>
              {/* metric pin */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center pointer-events-none">
                <Users size={18} weight="fill" style={{ color: hot.color }} />
                <span className="bg-black/80 text-white text-[10px] font-mono-data font-bold px-1.5 py-0.5 mt-0.5 whitespace-nowrap">
                  {m ? `${m.count} · ${m.dwell_minutes}m` : "0 · 0m"}
                </span>
              </div>
              {editing && (
                <button data-testid={`zone-remove-${i}`} onClick={(e) => { e.stopPropagation(); removeZone(i); }}
                  className="absolute -top-1 -right-1 bg-[#FF3B30] text-white rounded-full p-0.5 pointer-events-auto hover:scale-110 transition-transform">
                  <Trash size={11} weight="bold" />
                </button>
              )}
            </div>
          );
        })}

        {/* live draft rectangle */}
        {draft && (() => { const r = norm(draft); return (
          <div className="absolute border-2 border-dashed border-[#BF5AF2] bg-[#BF5AF2]/15 pointer-events-none"
            style={{ left: `${r.x1 * 100}%`, top: `${r.y1 * 100}%`, width: `${(r.x2 - r.x1) * 100}%`, height: `${(r.y2 - r.y1) * 100}%` }} />
        ); })()}

        {!hasHeat && src && (
          <div className="absolute bottom-2 left-2 text-[10px] font-mono-data text-[#A1A1AA] bg-black/60 px-2 py-1">
            Heat builds as shoppers are detected — none recorded yet for this range.
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mt-3 text-[10px] font-mono-data text-[#71717A] flex-wrap">
        <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ background: heatColor(0.9) }} /> Busy</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ background: heatColor(0.5) }} /> Moderate</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ background: heatColor(0.15) }} /> Quiet</span>
        <span className="ml-auto">{zones.length} section{zones.length === 1 ? "" : "s"} · each pin shows Traffic · Dwell(min)</span>
      </div>
    </div>
  );
}

function coldTip(zone) {
  const z = (zone || "").toLowerCase();
  if (z.includes("deli")) return "Run a tasting or combo/meal-deal display to pull shoppers to the deli counter.";
  if (z.includes("organic") || z.includes("produce") || z.includes("grape") || z.includes("onion") || z.includes("ref"))
    return "Brighten lighting and set a fresh, seasonal endcap here — produce sells on visibility.";
  if (z.includes("nut") || z.includes("dried")) return "Move a high-demand impulse item beside this aisle to lift dwell time.";
  if (z.includes("meat")) return "Feature a weekend butcher special on an endcap to draw shoppers to the case.";
  return "Add signage, a promo endcap, or reposition a staff member to draw shoppers in.";
}

function ColdZonesPanel({ zones }) {
  const ranked = [...zones].sort((a, b) => a.person_seconds - b.person_seconds);
  const cold = zones.some((z) => z.heat === "cold")
    ? ranked.filter((z) => z.heat === "cold")
    : ranked.slice(0, Math.max(1, Math.ceil(ranked.length / 3)));
  return (
    <div className="bg-[#121214] border border-white/10 p-4" data-testid="cold-zones-panel">
      <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1 flex items-center gap-2">
        <Snowflake size={16} weight="bold" className="text-[#0A84FF]" /> Cold Sections — Make Them Hot
      </h2>
      <p className="text-xs text-[#71717A] mb-3">Your least-shopped product sections and a concrete move to drive traffic into each.</p>
      {cold.length === 0 ? (
        <div className="text-xs text-[#71717A] font-mono-data py-8 text-center">No cold sections yet — data builds as the edge agent streams zone activity.</div>
      ) : (
        <div className="space-y-2" data-testid="cold-zone-list">
          {cold.map((z, i) => (
            <div key={`${z.camera_id}-${z.zone}`} data-testid={`cold-zone-${i}`} className="border border-[#0A84FF]/20 bg-[#0A84FF]/5 p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Snowflake size={14} weight="bold" className="text-[#0A84FF] shrink-0" />
                  <span className="font-medium text-sm truncate">{z.zone}</span>
                </div>
                <span className="font-mono-data text-[10px] text-[#71717A] shrink-0">{z.share}% of traffic</span>
              </div>
              <p className="text-xs text-[#A1A1AA] leading-snug flex items-start gap-1.5">
                <Lightbulb size={13} weight="fill" className="text-[#FF9F0A] mt-0.5 shrink-0" />
                {coldTip(z.zone)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TransitStrip({ transit, customersIn }) {
  return (
    <div className="bg-[#121214] border border-white/10 p-4" data-testid="transit-strip">
      <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1 flex items-center gap-2">
        <DoorOpen size={16} weight="bold" className="text-[#64D2FF]" /> Transit Areas (not ranked)
      </h2>
      <p className="text-xs text-[#71717A] mb-3">
        Entrance, exit, checkout &amp; office are busy by nature, so they're kept out of the hot/cold ranking.
        The entrance feeds your <span className="text-white">{customersIn.toLocaleString()} customers-in</span> count.
      </p>
      {transit.length === 0 ? (
        <div className="text-xs text-[#71717A] font-mono-data py-6 text-center">No transit areas detected yet.</div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="transit-list">
          {transit.map((z, i) => (
            <div key={`${z.camera_id}-${z.zone}`} data-testid={`transit-zone-${i}`}
              className="border border-[#64D2FF]/20 bg-[#64D2FF]/5 px-3 py-2 font-mono-data">
              <div className="text-xs text-[#D4D4D8]">{z.zone}</div>
              <div className="text-[10px] text-[#71717A] mt-0.5">{z.dwell_minutes}m · peak {z.peak}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HotZones() {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [cameras, setCameras] = useState([]);

  const load = useCallback(() => {
    api.get(`/analytics/hot-zones?period=${period}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [period]);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get("/cameras").then((r) => setCameras(r.data || [])).catch(() => {}); }, []);

  const zones = data?.zones || [];
  const transit = data?.transit_zones || [];
  const customersIn = data?.customers_in || 0;
  const maxSecs = zones[0]?.person_seconds || 1;
  const hottest = data?.hottest;

  return (
    <div className="space-y-4" data-testid="hot-zones-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-[#A1A1AA] max-w-2xl">
          See which <span className="text-white">product sections</span> run hot vs cold by traffic and dwell —
          draw sections right on the camera. Entrance, checkout &amp; exits are tracked separately as transit.
        </p>
        <div className="flex items-center border border-white/10" data-testid="hotzone-period-toggle">
          {PERIODS.map((p) => (
            <button key={p.id} data-testid={`hotzone-period-${p.id}`} onClick={() => setPeriod(p.id)}
              className={`px-3 py-1.5 text-[11px] font-mono-data uppercase tracking-wider transition-colors ${period === p.id ? "bg-[#FF3B30] text-black" : "text-[#A1A1AA] hover:text-white"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Highlight tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#121214] border border-white/10 p-4" data-testid="hotzone-customers-in">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-[#71717A]">Customers In</span>
            <DoorOpen size={18} weight="bold" style={{ color: "#64D2FF" }} />
          </div>
          <div className="font-mono-data text-2xl">{customersIn.toLocaleString()}</div>
          <div className="text-xs text-[#71717A] mt-1 font-mono-data truncate">
            {data?.entrance_cameras ? `from ${data.entrance_cameras} entrance cam${data.entrance_cameras === 1 ? "" : "s"}` : "set an entrance camera"}
          </div>
        </div>
        <div className="bg-[#121214] border border-white/10 p-4" data-testid="hotzone-hottest">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-[#71717A]">Hottest Section</span>
            <Fire size={18} weight="fill" style={{ color: "#FF3B30" }} />
          </div>
          <div className="font-mono-data text-2xl truncate">{hottest ? hottest.zone : "—"}</div>
          <div className="text-xs text-[#71717A] mt-1 font-mono-data truncate">
            {hottest ? `${hottest.camera} · ${hottest.dwell_minutes}m · ${hottest.share}%` : "No data yet"}
          </div>
        </div>
        <div className="bg-[#121214] border border-white/10 p-4" data-testid="hotzone-coldest">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-[#71717A]">Quietest Section</span>
            <Snowflake size={18} weight="bold" style={{ color: "#0A84FF" }} />
          </div>
          <div className="font-mono-data text-2xl truncate">{data?.coldest ? data.coldest.zone : "—"}</div>
          <div className="text-xs text-[#71717A] mt-1 font-mono-data truncate">
            {data?.coldest ? `${data.coldest.camera} · ${data.coldest.dwell_minutes}m` : "No data yet"}
          </div>
        </div>
        <div className="bg-[#121214] border border-white/10 p-4" data-testid="hotzone-count">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-widest text-[#71717A]">Sections Tracked</span>
            <MapPinArea size={18} weight="bold" style={{ color: "#BF5AF2" }} />
          </div>
          <div className="font-mono-data text-2xl">{zones.length}</div>
          <div className="text-xs text-[#71717A] mt-1 font-mono-data">across {cameras.length} cameras</div>
        </div>
      </div>

      {/* Hero: camera heatmap + drawable zones */}
      <StoreHeatmap cameras={cameras} period={period} />

      <TransitStrip transit={transit} customersIn={customersIn} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Ranked merchandise sections */}
        <div className="bg-[#121214] border border-white/10 p-4" data-testid="hotzone-ranking">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-3 flex items-center gap-2">
            <UsersThree size={16} weight="bold" className="text-[#FF9F0A]" /> Section Ranking
          </h2>
          {zones.length === 0 ? (
            <div className="text-xs text-[#71717A] font-mono-data py-8 text-center">
              No section activity yet — draw sections on the heatmap above (or keep the edge agent running) and the ranking builds automatically.
            </div>
          ) : (
            <div className="space-y-2">
              {zones.map((z, i) => {
                const pct = Math.max(3, Math.round((z.person_seconds / maxSecs) * 100));
                const hc = HEAT[z.heat] || HEAT.cold;
                return (
                  <div key={`${z.camera_id}-${z.zone}`} data-testid={`hotzone-row-${i}`} className="border border-white/5 p-2.5">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono-data text-xs text-[#71717A]">#{i + 1}</span>
                        <span className="font-medium text-sm truncate">{z.zone}</span>
                        <span className="font-mono-data text-[9px] px-1.5 py-0.5 border uppercase tracking-wider"
                          style={{ color: hc.color, borderColor: `${hc.color}66`, background: `${hc.color}1A` }}>{hc.label}</span>
                      </div>
                      <span className="font-mono-data text-[10px] text-[#71717A] shrink-0">{z.camera}</span>
                    </div>
                    <div className="h-2 bg-white/5 overflow-hidden">
                      <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: hc.color }} />
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 font-mono-data text-[10px] text-[#A1A1AA]">
                      <span className="flex items-center gap-1"><Clock size={11} /> {z.dwell_minutes}m</span>
                      <span>traffic {z.count}</span>
                      <span>peak {z.peak}</span>
                      <span className="ml-auto text-white">{z.share}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <ColdZonesPanel zones={zones} />
      </div>
    </div>
  );
}
